import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ResponsiveTable, TableCellBadge, TableCellActions, TableCellText } from '@/components/ui/responsive-table';
import { 
  FileUpIcon, 
  FileCheck, 
  FileX, 
  Download, 
  Filter, 
  Search, 
  CheckCircle, 
  XCircle, 
  MoreVertical,
  ChevronDown,
  Eye
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { Skeleton } from '@/components/ui/skeleton';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { PageContainer } from '@/components/layout/PageContainer';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';

// Types
type StatusType = 'pending' | 'approved' | 'rejected';

interface ParentUpload {
  id: string;
  organization_id: string;
  family_id: string;
  parent_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  status: StatusType;
  invited_email?: string;
  description?: string;
  created_at: string;
  // Join fields
  family_name?: string;
  parent_name?: string;
}

interface Family {
  id: string;
  name: string;
}

const statusColors = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: null },
  approved: { bg: 'bg-green-100', text: 'text-green-800', icon: FileCheck },
  rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: FileX },
};

const ParentDocuments = () => {
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;
  
  // State
  const [uploads, setUploads] = useState<ParentUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [families, setFamilies] = useState<Family[]>([]);
  const [selectedFamily, setSelectedFamily] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<StatusType | 'all'>('all');
  const [selectedDocument, setSelectedDocument] = useState<ParentUpload | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<StatusType>('pending');

  // Fetch data
  useEffect(() => {
    if (!organizationId) return;
    
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all families
        const { data: familiesData, error: familiesError } = await supabase
          .from('families')
          .select('id, name')
          .eq('organization_id', organizationId)
          .order('name');
          
        if (familiesError) throw familiesError;
        setFamilies(familiesData || []);
        
        // Fetch uploads with family and parent information
        const { data: uploadsData, error: uploadsError } = await supabase
          .from('parent_uploads')
          .select(`
            *,
            families(name),
            parents(first_name, last_name)
          `)
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false });
          
        if (uploadsError) throw uploadsError;
        
        // Process and normalize the data
        const processedUploads = (uploadsData || []).map(upload => ({
          ...upload,
          family_name: upload.families?.name || 'Unknown Family',
          parent_name: upload.parents 
            ? `${upload.parents.first_name} ${upload.parents.last_name}` 
            : 'Unknown Parent',
          status: (upload.status as StatusType) || 'pending'
        }));
        
        setUploads(processedUploads as ParentUpload[]);
      } catch (error) {
        console.error('Error fetching document data:', error);
        toast.error('Failed to load documents');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [organizationId]);
  
  // Filtered uploads
  const filteredUploads = uploads.filter(upload => {
    // Filter by status
    if (activeTab !== 'all' && upload.status !== activeTab) {
      return false;
    }
    
    // Filter by family
    if (selectedFamily && selectedFamily !== 'all' && upload.family_id !== selectedFamily) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        upload.file_name.toLowerCase().includes(query) ||
        upload.family_name?.toLowerCase().includes(query) ||
        upload.parent_name?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });
  
  // Handle download
  const handleDownload = async (upload: ParentUpload) => {
    try {
      // Create a signed URL for the private bucket
      const { data: signedUrlData, error } = await supabase.storage
        .from('parent-documents')
        .createSignedUrl(upload.file_path, 60); // 60 seconds expiry

      if (error) throw error;
      if (!signedUrlData?.signedUrl) throw new Error('Failed to generate download URL');

      // Open the signed URL in a new tab to trigger download
      const a = document.createElement('a');
      a.href = signedUrlData.signedUrl;
      a.download = upload.file_name;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };
  
  // Handle status change
  const handleStatusChange = async () => {
    if (!selectedDocument) return;
    
    try {
      const { error } = await supabase
        .from('parent_uploads')
        .update({ status: newStatus })
        .eq('id', selectedDocument.id);
        
      if (error) throw error;
      
      // Update local state
      setUploads(prev => 
        prev.map(upload => 
          upload.id === selectedDocument.id 
            ? { ...upload, status: newStatus } 
            : upload
        )
      );
      
      toast.success(`Document status updated to ${newStatus}`);
      setIsStatusModalOpen(false);
    } catch (error) {
      console.error('Error updating document status:', error);
      toast.error('Failed to update document status');
    }
  };
  
  // Open view modal
  const openViewModal = (upload: ParentUpload) => {
    setSelectedDocument(upload);
    setIsViewModalOpen(true);
  };
  
  // Open status change modal
  const openStatusModal = (upload: ParentUpload, status: StatusType) => {
    setSelectedDocument(upload);
    setNewStatus(status);
    setIsStatusModalOpen(true);
  };
  
  return (
    <div className="space-y-6">
      <StandardPageHeader
        title="Parent Document Management"
        subtitle="Review and manage documents uploaded by parents"
        icon={FileUpIcon}
      />
      
      <div className="w-full space-y-6">
      
      {/* Filter Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search documents, families, or parents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>
            
            <div className="w-full md:w-64">
              <Select value={selectedFamily || 'all'} onValueChange={value => setSelectedFamily(value === 'all' ? '' : value)}>
                <SelectTrigger>
                  <div className="flex items-center">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter by family" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Families</SelectItem>
                  {families.map(family => (
                    <SelectItem key={family.id} value={family.id}>
                      {family.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Status Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as StatusType | 'all')}>
        <TabsList className="grid grid-cols-4 mb-4">
          <TabsTrigger value="all">
            All
            <Badge variant="outline" className="ml-2">
              {uploads.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="pending">
            Pending
            <Badge variant="outline" className="ml-2 bg-yellow-50">
              {uploads.filter(u => u.status === 'pending').length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="approved">
            Approved
            <Badge variant="outline" className="ml-2 bg-green-50">
              {uploads.filter(u => u.status === 'approved').length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="rejected">
            Rejected
            <Badge variant="outline" className="ml-2 bg-red-50">
              {uploads.filter(u => u.status === 'rejected').length}
            </Badge>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab} className="mt-0">
          {loading ? (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : filteredUploads.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center">
                <FileUpIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium mb-2">No Documents Found</h3>
                <p className="text-muted-foreground">
                  {searchQuery || selectedFamily 
                    ? "Try adjusting your filters" 
                    : "No parent documents have been uploaded yet"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <ResponsiveTable
              data={filteredUploads}
              columns={[
                {
                  key: 'file_name',
                  header: 'File Name',
                  priority: 'high',
                  cell: (upload) => (
                    <span className="font-medium break-all">{upload.file_name}</span>
                  )
                },
                {
                  key: 'family_name',
                  header: 'Family',
                  priority: 'high',
                  cell: (upload) => upload.family_name || 'Unknown Family'
                },
                {
                  key: 'parent_name',
                  header: 'Parent',
                  priority: 'medium',
                  cell: (upload) => upload.parent_name || 'Unknown Parent'
                },
                {
                  key: 'created_at',
                  header: 'Upload Date',
                  priority: 'medium',
                  cell: (upload) => format(new Date(upload.created_at), 'MMM d, yyyy')
                },
                {
                  key: 'status',
                  header: 'Status',
                  priority: 'high',
                  cell: (upload) => {
                    const statusStyle = statusColors[upload.status] || statusColors.pending;
                    const StatusIcon = statusStyle.icon;
                    return (
                      <TableCellBadge>
                        {StatusIcon && <StatusIcon className="w-3 h-3 mr-1" />}
                        {upload.status.charAt(0).toUpperCase() + upload.status.slice(1)}
                      </TableCellBadge>
                    );
                  }
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  priority: 'high',
                  cell: (upload) => (
                    <TableCellActions>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openViewModal(upload)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownload(upload)}>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </DropdownMenuItem>
                          {upload.status !== 'approved' && (
                            <DropdownMenuItem onClick={() => openStatusModal(upload, 'approved')}>
                              <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                              Approve
                            </DropdownMenuItem>
                          )}
                          {upload.status !== 'rejected' && (
                            <DropdownMenuItem onClick={() => openStatusModal(upload, 'rejected')}>
                              <XCircle className="h-4 w-4 mr-2 text-red-600" />
                              Reject
                            </DropdownMenuItem>
                          )}
                          {upload.status !== 'pending' && (
                            <DropdownMenuItem onClick={() => openStatusModal(upload, 'pending')}>
                              <ChevronDown className="h-4 w-4 mr-2 text-yellow-600" />
                              Reset to Pending
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCellActions>
                  )
                }
              ]}
              cardTitle={(upload) => upload.file_name}
              cardSubtitle={(upload) => upload.family_name || 'Unknown Family'}
              cardActions={(upload) => (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openViewModal(upload)}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDownload(upload)}>
                    <Download className="h-4 w-4" />
                  </Button>
                  {upload.status === 'pending' && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => openStatusModal(upload, 'approved')}>
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openStatusModal(upload, 'rejected')}>
                        <XCircle className="h-4 w-4 text-red-600" />
                      </Button>
                    </>
                  )}
                </div>
              )}
              emptyMessage="No parent documents found"
            />
          )}
        </TabsContent>
      </Tabs>
      
      {/* View Document Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Document Details</DialogTitle>
          </DialogHeader>
          
          {selectedDocument && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">File Name</h3>
                <p className="break-all">{selectedDocument.file_name}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Family</h3>
                  <p>{selectedDocument.family_name}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Parent</h3>
                  <p>{selectedDocument.parent_name}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Upload Date</h3>
                  <p>{format(new Date(selectedDocument.created_at), 'MMM d, yyyy')}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">File Size</h3>
                  <p>
                    {selectedDocument.file_size < 1024 * 1024
                      ? `${Math.round(selectedDocument.file_size / 1024)} KB`
                      : `${Math.round(selectedDocument.file_size / (1024 * 1024) * 10) / 10} MB`}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">File Type</h3>
                  <p>{selectedDocument.file_type}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Status</h3>
                  <Badge className={`${statusColors[selectedDocument.status].bg} ${statusColors[selectedDocument.status].text}`}>
                    {selectedDocument.status.charAt(0).toUpperCase() + selectedDocument.status.slice(1)}
                  </Badge>
                </div>
              </div>
              
              {selectedDocument.description && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Description</h3>
                  <p>{selectedDocument.description}</p>
                </div>
              )}
              
              <div className="flex gap-2 pt-2">
                <Button className="flex-1" onClick={() => handleDownload(selectedDocument)}>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                
                {selectedDocument.status !== 'approved' && (
                  <Button variant="outline" className="flex-1" onClick={() => {
                    setIsViewModalOpen(false);
                    openStatusModal(selectedDocument, 'approved');
                  }}>
                    <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                    Approve
                  </Button>
                )}
                
                {selectedDocument.status !== 'rejected' && (
                  <Button variant="outline" className="flex-1" onClick={() => {
                    setIsViewModalOpen(false);
                    openStatusModal(selectedDocument, 'rejected');
                  }}>
                    <XCircle className="h-4 w-4 mr-2 text-red-600" />
                    Reject
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Change Status Modal */}
      <Dialog open={isStatusModalOpen} onOpenChange={setIsStatusModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {newStatus === 'approved' 
                ? 'Approve Document' 
                : newStatus === 'rejected'
                ? 'Reject Document'
                : 'Reset Document Status'}
            </DialogTitle>
            <DialogDescription>
              {newStatus === 'approved' 
                ? 'This document will be marked as approved.' 
                : newStatus === 'rejected'
                ? 'This document will be marked as rejected.'
                : 'This document will be reset to pending status.'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedDocument && (
            <div>
              <p className="mb-4">
                <span className="font-medium">File name:</span> {selectedDocument.file_name}
              </p>
              <p className="mb-4">
                <span className="font-medium">Family:</span> {selectedDocument.family_name}
              </p>
              
              <DialogFooter className="sm:justify-start">
                <Button
                  type="button"
                  variant={newStatus === 'approved' ? 'default' : newStatus === 'rejected' ? 'destructive' : 'outline'}
                  onClick={handleStatusChange}
                >
                  {newStatus === 'approved' && <CheckCircle className="h-4 w-4 mr-2" />}
                  {newStatus === 'rejected' && <XCircle className="h-4 w-4 mr-2" />}
                  {newStatus === 'pending' && <ChevronDown className="h-4 w-4 mr-2" />}
                  
                  {newStatus === 'approved' 
                    ? 'Approve Document' 
                    : newStatus === 'rejected'
                    ? 'Reject Document'
                    : 'Reset to Pending'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsStatusModalOpen(false)}
                >
                  Cancel
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default ParentDocuments; 